// MCP Security — Endpoints list.
//
// Rebuilt 2026-10-12 to strictly follow the Claude Design mockup
// "MCP Endpoints.dc.html". Dense table with:
//   - title row (icon + title + Download CSV + Onboard endpoint)
//   - filter card (search + state dropdown + transport dropdown + refresh)
//   - table card (header + rows + pagination)
//   - per-row kebab menu (Configure / Re-probe / Pause / Copy / Offboard)
//   - 460 px right-slide drawer on row click
//   - toast on all actions
//
// Mounted at /v2/mcp/endpoints.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Alert } from "@mui/material";

import V2PageHeader from "../../../../common/V2PageHeader";
import axios from "../../../../../utils/axios/v1/userAxios";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import { useNotification } from "../../../../../contexts/NotificationContext";

// ─── design tokens (strict from mockup) ────────────────────────────────
// Aligned 2026-10-12 with the WAF / BM visual language — the title
// uses the shared PageTitle color (neutral[1000] = #111111), buttons
// use the WAF button height (36 px) and base font (14 px), and all
// borders resolve to WAF's canonical #E5E5E5.
const INDIGO       = "#6371F7";  // == colors.primary[300]
const INDIGO_INK   = "#454FAD";  // == colors.primary[500]
const INDIGO_SOFT  = "#EFF1FE";  // == colors.primary[100]
const INDIGO_DEEP  = "#454FAD";  // folds to primary[500] (was #2E3A8C)
const INDIGO_400   = "#4F5AC6";  // == colors.primary[400]
const INK          = "#111111";  // == colors.neutral[1000] — WAF page title ink
const INK_2        = "#2B2B2B";  // == colors.neutral[900]
const MUTE         = "#606060";  // == colors.neutral[700]
const MUTE_2       = "#7B7B7B";  // == colors.neutral[600]
const MUTE_3       = "#959595";  // == colors.neutral[500]
const BORDER       = "#E5E5E5";  // == colors.neutral[200] — WAF canonical border
const BORDER_2     = "#E5E5E5";  // folded onto BORDER for a single visible gray
const BORDER_SOFT  = "#F8F9FA";  // == colors.neutral[50]
const BG_SOFT      = "#F8F9FA";  // == colors.neutral[50]
const BG_HOVER     = "#EBEDF5";  // == colors.primary.alpha

// state → pill colours (verbatim from design)
const STATES = {
  verified:    { label: "Verified",    bg: "#EEFBF3", border: "#A6E5C2", fg: "#15965A", dot: "#1FC16B" },
  drift_hold:  { label: "Drift hold",  bg: "#FFFBEA", border: "#EBC84A", fg: "#A37B00", dot: "#F2B70A" },
  probing:     { label: "Probing",     bg: "#F0F2FF", border: "#C3C9FB", fg: "#4D5BE0", dot: "#6371F7" },
  unreachable: { label: "Unreachable", bg: "#FFF1F2", border: "#FDB8BE", fg: "#D92D3B", dot: "#FB3748" },
  paused:      { label: "Paused",      bg: "#F4F4F4", border: "#D4D4D4", fg: "#5C5C5C", dot: "#A3A3A3" },
};

// map our CP state → design state slug
const mapState = (ep) => {
  if (ep.drift_hold) return "drift_hold";
  switch (ep.state) {
    case "verified":    return "verified";
    case "verifying":   return "probing";
    case "configured":  return "probing";
    case "stale":       return "unreachable";
    case "disabled":    return "paused";
    default:            return "probing";
  }
};

const mapTransport = (t) => {
  switch ((t || "").toLowerCase()) {
    case "sse":        return "SSE";
    case "http":       return "Streamable HTTP";
    case "websocket":  return "WebSocket";
    case "stdio":      return "stdio bridge";
    default:           return t || "SSE";
  }
};

const PROTECTION_MODULES = [
  "Prompt-injection scan",
  "Credential scan",
  "Tool pinning",
  "Session integrity",
  "Egress allowlist",
  "PII redaction",
  "Rate limiting",
];

const PER_PAGE_OPTS = [5, 10, 25];

const fmtNum = (n) => (n == null ? "–" : Number(n).toLocaleString("en-US"));

const relTime = (iso) => {
  if (!iso) return "–";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "–";
  const d = Date.now() - t;
  const s = Math.floor(d / 1000);
  if (s < 10) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const splitHostPath = (pattern) => {
  if (!pattern) return { host: "(detect)", path: "" };
  if (pattern.startsWith("/")) return { host: "", path: pattern };
  const slash = pattern.indexOf("/");
  if (slash === -1) return { host: pattern, path: "" };
  return { host: pattern.slice(0, slash), path: pattern.slice(slash) };
};

// ─── root ─────────────────────────────────────────────────────────────
export default function McpEndpointsList() {
  const { effectiveSiteUid } = useGlobalSite();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [eps, setEps]           = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState(null);

  const [q, setQ]                   = useState("");
  const [filterState, setFilterState] = useState(null);  // slug or null
  const [filterTransport, setFilterTransport] = useState(null); // display label or null
  const [menu, setMenu]             = useState(null);    // 'state' | 'tr' | 'per' | <endpoint_id>
  const [page, setPage]             = useState(0);
  const [per, setPer]               = useState(10);
  const [sel, setSel]               = useState(null);    // endpoint_id (drawer)
  const [toast, setToast]           = useState("");
  const toastTimer = useRef(null);

  const flash = (t) => {
    clearTimeout(toastTimer.current);
    setToast(t);
    toastTimer.current = setTimeout(() => setToast(""), 2200);
  };

  const load = async () => {
    if (!effectiveSiteUid) return;
    setLoading(true);
    setErr(null);
    try {
      const [epsR, ovR] = await Promise.all([
        axios.get(`mcp/sites/${effectiveSiteUid}/endpoints`),
        axios.get(`mcp/sites/${effectiveSiteUid}/overview?hours=24`).catch(() => ({ data: null })),
      ]);
      setEps(epsR.data?.items || []);
      setOverview(ovR.data || null);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [effectiveSiteUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // overview top_endpoints → risk / decisions by endpoint_id
  const aggByEp = useMemo(() => {
    const m = {};
    (overview?.top_endpoints || []).forEach((e) => {
      m[e.endpoint_id] = { risk: e.risk_score, dec: e.decisions, blk: e.blocks };
    });
    return m;
  }, [overview]);

  // build augmented row set
  const augmented = useMemo(() => (
    (eps || []).map((e) => {
      const { host, path } = splitHostPath(e.location_pattern || e.endpoint_url || "");
      const agg = aggByEp[e.endpoint_id] || {};
      const stateSlug = mapState(e);
      const transport = mapTransport(e.transport);
      const toolsCount = (e.tools_approved_count ?? e.tools_count ?? 0) + (e.tools_pending_count ?? 0);
      return {
        ...e,
        host, path, transport, stateSlug, toolsCount,
        risk: Number.isFinite(agg.risk) ? agg.risk : null,
        dec:  Number.isFinite(agg.dec)  ? agg.dec  : null,
        blk:  Number.isFinite(agg.blk)  ? agg.blk  : 0,
        lastProbeAt: e.last_verified_at || e.last_probe?.started_at || e.updated_at,
        addedAt: e.created_at,
        modules: e.protection_modules || [],
      };
    })
  ), [eps, aggByEp]);

  const transportsAvailable = useMemo(() => {
    const s = new Set(augmented.map((e) => e.transport));
    return [...s];
  }, [augmented]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return augmented.filter((e) => {
      if (filterState && e.stateSlug !== filterState) return false;
      if (filterTransport && e.transport !== filterTransport) return false;
      if (ql) {
        const hay = `${e.host}${e.path} ${e.endpoint_id || ""}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [augmented, q, filterState, filterTransport]);

  const pages = Math.max(1, Math.ceil(filtered.length / per));
  const pg = Math.min(page, pages - 1);
  const slice = filtered.slice(pg * per, pg * per + per);

  // ─── actions ─────────────────────────────────────────────────────────
  const reprobeOne = async (id) => {
    try {
      await axios.post(`mcp/sites/${effectiveSiteUid}/endpoints/${id}/reprobe`);
      flash("Re-probe queued");
      load();
    } catch (e) {
      flash(e?.response?.data?.message || "Re-probe failed");
    }
    setMenu(null);
  };
  const pauseOne = async (ep) => {
    try {
      const newState = ep.state === "disabled" ? "verified" : "disabled";
      await axios.patch(`mcp/endpoints/${ep.endpoint_id}`, { state: newState });
      flash(newState === "disabled" ? "Enforcement paused" : "Enforcement resumed");
      load();
    } catch (e) {
      flash(e?.response?.data?.message || "Pause toggle failed");
    }
    setMenu(null);
  };
  const copyId = (id) => {
    try { navigator.clipboard?.writeText(id); } catch (_) {}
    flash("Endpoint id copied");
    setMenu(null);
  };
  const offboardOne = async (ep) => {
    if (!window.confirm(`Offboard ${ep.host}${ep.path}?`)) return;
    try {
      await axios.delete(`mcp/sites/${effectiveSiteUid}/endpoints/${ep.endpoint_id}`);
      flash(`${ep.host}${ep.path} offboarded`);
      setMenu(null);
      load();
    } catch (e) {
      flash(e?.response?.data?.message || "Offboard failed");
    }
  };
  const downloadCsv = () => {
    const cols = ["endpoint_id", "host", "path", "transport", "state", "tools", "risk_24h", "decisions_24h", "blocks_24h", "last_probe", "added_at"];
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const rows = augmented.map((e) => [
      e.endpoint_id, e.host, e.path, e.transport, e.stateSlug,
      e.toolsCount, e.risk ?? "", e.dec ?? "", e.blk ?? 0,
      e.lastProbeAt || "", e.addedAt || "",
    ].map(esc).join(","));
    const blob = new Blob([cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `mcp-endpoints-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    flash("Endpoint list downloaded (CSV)");
  };

  // ─── render guard ───────────────────────────────────────────────────
  if (!effectiveSiteUid) {
    return (
      <>
        <Helmet><title>MCP Security · Endpoints</title></Helmet>
        <Alert severity="info" sx={{ mt: 2 }}>Select a site from the header.</Alert>
      </>
    );
  }

  const selEp = augmented.find((e) => e.endpoint_id === sel);

  return (
    <>
      <Helmet><title>MCP Security · Endpoints</title></Helmet>
      {/* V2PageHeader is skipped — the design has its own in-page title row */}
      <Box sx={{ p: "28px 20px 40px 28px", display: "flex", flexDirection: "column", gap: 3.25 }}>

        {/* Title row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: "1 1 240px" }}>
            <PlugIcon size={22} color={INDIGO} />
            {/* Typography mirrors PageTitle (24/32 medium neutral[1000]) so the row
                reads as the same V2 page-header line used in WAF/BM. */}
            <Typography sx={{ fontSize: 24, lineHeight: "32px", fontWeight: "medium", color: INK }}>
              Endpoints
            </Typography>
          </Box>
          <OutlineBtn icon={<DownloadIcon size={16} />} label="Download endpoint list" onClick={downloadCsv} />
          <PrimaryBtn icon={<PlusIcon size={16} />} label="Onboard endpoint" onClick={() => navigate("/v2/mcp/onboard")} />
        </Box>

        {/* Filter bar */}
        <Box sx={{
          mt: "-6px",
          border: `1px solid ${BORDER}`,
          borderRadius: "8px",
          p: "12px 10px",
          display: "flex",
          alignItems: "center",
          gap: 1.75,
          flexWrap: "wrap",
          position: "relative",
          zIndex: 2,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 15, fontWeight: 500, color: INK }}>
            <FilterIcon size={16} color={INDIGO_DEEP} /> Filters
          </Box>

          {/* search */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.25,
            height: 36, width: 352, maxWidth: "100%",
            p: "0 12px 0 16px",
            border: `1px solid ${BORDER_2}`, borderRadius: "6px",
            background: "#fff",
          }}>
            <SearchIcon size={14} color={MUTE} />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(0); }}
              placeholder="Search hostname, path, or endpoint id…"
              style={{ flex: 1, minWidth: 0, border: "none", outline: "none", fontSize: 15, color: INK, background: "transparent", fontFamily: "inherit" }}
            />
          </Box>

          {/* state dropdown */}
          <Dropdown
            open={menu === "state"}
            onToggle={() => setMenu(menu === "state" ? null : "state")}
            onClose={() => setMenu(null)}
            selected={filterState ? { label: STATES[filterState].label, color: STATES[filterState].dot } : null}
            placeholder={<><CircleDotIcon size={15} /> State <ChevronDownIcon size={14} color={MUTE} /></>}
            onClear={() => { setFilterState(null); setPage(0); }}
          >
            {Object.entries(STATES).map(([k, s]) => (
              <DropdownRow
                key={k}
                onClick={() => { setFilterState(k); setPage(0); setMenu(null); }}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.dot, display: "inline-block" }} />
                <span style={{ flex: 1 }}>{s.label}</span>
                <span style={{ fontSize: 12, color: MUTE_2 }}>{augmented.filter((e) => e.stateSlug === k).length}</span>
              </DropdownRow>
            ))}
          </Dropdown>

          {/* transport dropdown */}
          <Dropdown
            open={menu === "tr"}
            onToggle={() => setMenu(menu === "tr" ? null : "tr")}
            onClose={() => setMenu(null)}
            selected={filterTransport ? { label: filterTransport } : null}
            placeholder={<><RadioIcon size={15} /> Transport <ChevronDownIcon size={14} color={MUTE} /></>}
            onClear={() => { setFilterTransport(null); setPage(0); }}
          >
            <DropdownRow onClick={() => { setFilterTransport(null); setPage(0); setMenu(null); }}>
              <span style={{ flex: 1 }}>All transports</span>
              {!filterTransport && <CheckIcon size={14} color={INDIGO} />}
            </DropdownRow>
            {transportsAvailable.map((t) => (
              <DropdownRow key={t} onClick={() => { setFilterTransport(t); setPage(0); setMenu(null); }}>
                <span style={{ flex: 1 }}>{t}</span>
                {filterTransport === t && <CheckIcon size={14} color={INDIGO} />}
              </DropdownRow>
            ))}
          </Dropdown>

          <Box sx={{ flex: 1 }} />

          {/* refresh */}
          <IconBtn44 onClick={load} title="Refresh">
            <RotateCwIcon size={18} color={INDIGO} />
          </IconBtn44>
        </Box>

        {err && <Alert severity="error">{err}</Alert>}

        {/* Table card */}
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "8px", p: "18px 16px 16px", overflowX: "auto", position: "relative" }}>
          <Box sx={{ minWidth: 1040 }}>
            {/* header row */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: "minmax(260px, 2.6fr) minmax(110px, 1fr) minmax(130px, 1.1fr) minmax(70px, .7fr) minmax(110px, 1fr) minmax(120px, 1fr) minmax(140px, 1.2fr) 70px",
              alignItems: "center",
              p: "12px 14px",
              fontSize: 15,
              fontWeight: 500,
              color: INK,
            }}>
              <span>Endpoint</span>
              <span>Transport</span>
              <span>State</span>
              <span>Tools</span>
              <span>Risk 24h</span>
              <span>Last probe</span>
              <span>Decisions 24h</span>
              <span style={{ textAlign: "right" }}>Action</span>
            </Box>

            {slice.map((e) => {
              const s = STATES[e.stateSlug];
              const riskDot = e.risk == null ? "transparent"
                : e.risk >= 50 ? "#FB3748"
                : e.risk >= 25 ? "#F2B70A"
                : "#1FC16B";
              const isMenu = menu === e.endpoint_id;
              const isSel = sel === e.endpoint_id;
              return (
                <Box
                  key={e.endpoint_id}
                  onClick={() => setSel(e.endpoint_id)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(260px, 2.6fr) minmax(110px, 1fr) minmax(130px, 1.1fr) minmax(70px, .7fr) minmax(110px, 1fr) minmax(120px, 1fr) minmax(140px, 1.2fr) 70px",
                    alignItems: "center",
                    minHeight: 52,
                    p: "6px 14px",
                    fontSize: 15,
                    cursor: "pointer",
                    borderRadius: "4px",
                    background: isSel ? INDIGO_SOFT : "#fff",
                    "&:hover": { background: isSel ? INDIGO_SOFT : BG_HOVER },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: INK }}>
                      {e.host}<span style={{ color: MUTE }}>{e.path}</span>
                    </Box>
                    <Box sx={{ fontFamily: "monospace", fontSize: 12, lineHeight: "16px", color: MUTE_2 }}>
                      {(e.endpoint_id || "").slice(0, 8)}…
                    </Box>
                  </Box>
                  <span style={{ color: INK }}>{e.transport}</span>
                  <span>
                    <span style={{
                      display: "inline-flex", alignItems: "center",
                      height: 26, padding: "0 14px",
                      borderRadius: 999,
                      border: `1px solid ${s.border}`,
                      background: s.bg, color: s.fg,
                      fontSize: 15, whiteSpace: "nowrap",
                    }}>{s.label}</span>
                  </span>
                  <span style={{ color: INK }}>{e.toolsCount}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: INK }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: riskDot }} />
                    {e.risk == null ? "–" : e.risk}
                  </span>
                  <span style={{ color: INK }}>{relTime(e.lastProbeAt)}</span>
                  <span style={{ color: INK }}>
                    {fmtNum(e.dec)}
                    {e.blk > 0 && <span style={{ color: "#D92D3B", fontSize: 13 }}>{` · ${e.blk} blocked`}</span>}
                  </span>
                  <Box sx={{ position: "relative", display: "flex", justifyContent: "flex-end" }} onClick={(ev) => ev.stopPropagation()}>
                    <button
                      onClick={() => setMenu(isMenu ? null : e.endpoint_id)}
                      style={{
                        width: 32, height: 32, border: "none", background: "transparent",
                        borderRadius: 6, display: "grid", placeItems: "center", cursor: "pointer",
                        color: INK,
                      }}
                      onMouseEnter={(ev) => (ev.currentTarget.style.background = "#EDEDED")}
                      onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}
                    >
                      <EllipsisVIcon size={18} />
                    </button>
                    {isMenu && (
                      <Box sx={{
                        position: "absolute", top: 36, right: 0, zIndex: 8, width: 200,
                        background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px",
                        boxShadow: "0 8px 24px rgba(0,0,0,.08)", p: "4px",
                      }}>
                        <MenuRow icon={<Settings2Icon size={15} />} onClick={() => { setSel(e.endpoint_id); setMenu(null); }}>Configure</MenuRow>
                        <MenuRow icon={<RotateCcwIcon size={15} />} onClick={() => reprobeOne(e.endpoint_id)}>Re-probe</MenuRow>
                        <MenuRow
                          icon={e.state === "disabled" ? <PlayIcon size={15} /> : <PauseIcon size={15} />}
                          onClick={() => pauseOne(e)}
                        >{e.state === "disabled" ? "Resume enforcement" : "Pause enforcement"}</MenuRow>
                        <MenuRow icon={<CopyIcon size={15} />} onClick={() => copyId(e.endpoint_id)}>Copy endpoint id</MenuRow>
                        <Box sx={{ height: 1, background: "#EDEDED", m: "4px 0" }} />
                        <MenuRow icon={<Trash2Icon size={15} />} onClick={() => offboardOne(e)} danger>Offboard</MenuRow>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}

            {!loading && slice.length === 0 && (
              <Box sx={{ p: "40px", textAlign: "center", color: MUTE, fontSize: 15 }}>
                No endpoints match these filters.
              </Box>
            )}

            {/* Pagination */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3.5, p: "26px 14px 4px", fontSize: 15, color: INK_2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, position: "relative" }}>
                Rows per page:
                <span
                  onClick={() => setMenu(menu === "per" ? null : "per")}
                  style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: INK }}
                >
                  {per}
                  <ChevronDownIcon size={14} />
                </span>
                {menu === "per" && (
                  <Box sx={{
                    position: "absolute", bottom: 30, right: 0, zIndex: 8,
                    background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px",
                    boxShadow: "0 8px 24px rgba(0,0,0,.08)", p: "4px",
                  }}>
                    {PER_PAGE_OPTS.map((n) => (
                      <Box
                        key={n}
                        onClick={() => { setPer(n); setPage(0); setMenu(null); }}
                        sx={{
                          padding: "6px 16px", borderRadius: "6px", cursor: "pointer",
                          "&:hover": { background: BG_SOFT },
                        }}
                      >{n}</Box>
                    ))}
                  </Box>
                )}
              </Box>
              <span>{filtered.length ? `${pg * per + 1}–${pg * per + slice.length} of ${filtered.length}` : "0 of 0"}</span>
              <Box sx={{ display: "flex", gap: 2.25 }}>
                <ChevronLeftIcon size={18} color={pg > 0 ? INK : "#C4C4C4"} onClick={pg > 0 ? () => setPage(pg - 1) : undefined} style={{ cursor: pg > 0 ? "pointer" : "default" }} />
                <ChevronRightIcon size={18} color={pg < pages - 1 ? INK : "#C4C4C4"} onClick={pg < pages - 1 ? () => setPage(pg + 1) : undefined} style={{ cursor: pg < pages - 1 ? "pointer" : "default" }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* click-away overlay for dropdowns */}
      {menu && (
        <Box onClick={() => setMenu(null)} sx={{ position: "fixed", inset: 0, zIndex: 6 }} />
      )}

      {/* Right drawer */}
      {selEp && (
        <>
          <Box onClick={() => setSel(null)} sx={{ position: "fixed", inset: 0, background: "rgba(17,17,17,.35)", zIndex: 20 }} />
          <Box component="aside" sx={{
            position: "fixed", top: 0, right: 0, bottom: 0,
            width: "min(460px, 100vw)",
            background: "#fff", zIndex: 21,
            borderLeft: `1px solid ${BORDER}`,
            display: "flex", flexDirection: "column",
          }}>
            {/* drawer header */}
            <Box sx={{
              p: "20px 24px",
              borderBottom: `1px solid ${BORDER}`,
              display: "flex", alignItems: "flex-start", gap: 1.5,
            }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, color: MUTE }}>Endpoint configuration</Typography>
                <Typography sx={{ fontSize: 18, lineHeight: "26px", fontWeight: 500, wordBreak: "break-all", color: INK }}>
                  {selEp.host}{selEp.path}
                </Typography>
                <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: MUTE_2, wordBreak: "break-all" }}>
                  {selEp.endpoint_id}
                </Typography>
              </Box>
              <button
                onClick={() => setSel(null)}
                style={{
                  width: 32, height: 32, border: "none", background: "transparent",
                  borderRadius: 6, cursor: "pointer", display: "grid", placeItems: "center",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = BG_SOFT)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <XIcon size={18} />
              </button>
            </Box>

            {/* drawer body */}
            <Box sx={{ flex: 1, overflowY: "auto", p: "16px 24px", display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* state + transport pills */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {(() => {
                  const s = STATES[selEp.stateSlug];
                  return (
                    <span style={{
                      display: "inline-flex", alignItems: "center",
                      height: 26, padding: "0 14px", borderRadius: 999,
                      border: `1px solid ${s.border}`, background: s.bg, color: s.fg,
                      fontSize: 14,
                    }}>{s.label}</span>
                  );
                })()}
                <span style={{
                  display: "inline-flex", alignItems: "center",
                  height: 26, padding: "0 12px", borderRadius: 999,
                  border: `1px solid ${BORDER_2}`, fontSize: 14, color: INK,
                }}>{selEp.transport}</span>
              </Box>

              {/* facts list */}
              <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "8px" }}>
                {[
                  ["Origin host",       selEp.host || "(detect)"],
                  ["Path",              selEp.path || "–"],
                  ["Onboarded",         selEp.addedAt ? new Date(selEp.addedAt).toLocaleString("en-GB") : "–"],
                  ["Last probe",        relTime(selEp.lastProbeAt)],
                  ["Tools discovered",  String(selEp.toolsCount)],
                  ["Risk 24 h",         selEp.risk == null ? "–" : String(selEp.risk)],
                  ["Decisions 24 h",    selEp.dec == null ? "–" : `${fmtNum(selEp.dec)}${selEp.blk ? ` (${selEp.blk} blocked)` : ""}`],
                ].map(([k, v], i) => (
                  <Box key={k} sx={{
                    display: "flex", gap: 1.5,
                    p: "11px 14px",
                    borderTop: i === 0 ? "none" : `1px solid ${BORDER_SOFT}`,
                    fontSize: 14,
                  }}>
                    <span style={{ width: 140, flex: "none", color: MUTE }}>{k}</span>
                    <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word", color: INK }}>{v}</span>
                  </Box>
                ))}
              </Box>

              {/* protection modules */}
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 500, mb: 1, color: INK }}>Protection modules</Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {PROTECTION_MODULES.map((m) => {
                    const on = selEp.modules?.includes?.(m);
                    return (
                      <span key={m} style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        height: 28, padding: "0 12px", borderRadius: 999,
                        border: `1px solid ${BORDER_2}`, fontSize: 13, color: INK,
                      }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: on ? "#1FC16B" : BORDER_2 }} />
                        {m}
                      </span>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* drawer footer */}
            <Box sx={{ p: "14px 24px", borderTop: `1px solid ${BORDER}`, display: "flex", gap: 1.25, flexWrap: "wrap" }}>
              <PrimaryBtn
                icon={<Settings2Icon size={16} />}
                label="Open configuration"
                onClick={() => { navigate(`/v2/mcp/configuration#${selEp.endpoint_id}`); setSel(null); }}
              />
              <OutlineBtn
                icon={<RotateCcwIcon size={16} />}
                label="Re-probe"
                onClick={() => reprobeOne(selEp.endpoint_id)}
              />
              <GhostBtn
                icon={selEp.state === "disabled" ? <PlayIcon size={16} /> : <PauseIcon size={16} />}
                label={selEp.state === "disabled" ? "Resume enforcement" : "Pause enforcement"}
                onClick={() => pauseOne(selEp)}
              />
            </Box>
          </Box>
        </>
      )}

      {/* Toast */}
      {toast && (
        <Box sx={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          zIndex: 30, background: INK, color: "#fff", fontSize: 14,
          p: "10px 16px", borderRadius: "8px",
          display: "flex", alignItems: "center", gap: 1,
        }}>
          <CheckIcon size={15} color="#4ADE80" />
          {toast}
        </Box>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Primitives
// ═══════════════════════════════════════════════════════════════════════

// Button components below adopt WAF primaryButtonStyle /
// secondaryButtonStyle values: 36 px tall, 8 px radius, 14 px font,
// indigo #6371F7 primary, neutral #E5E7EB border secondary.
function PrimaryBtn({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 36, padding: "0 14px",
        border: `1px solid ${INDIGO}`, background: INDIGO, color: "#fff",
        borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer",
        display: "flex", alignItems: "center", gap: 8,
        fontFamily: "inherit",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "#5563E8"; e.currentTarget.style.borderColor = "#5563E8"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = INDIGO; e.currentTarget.style.borderColor = INDIGO; }}
    >
      {icon}{label}
    </button>
  );
}
function OutlineBtn({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 36, padding: "0 14px",
        border: `1px solid #E5E7EB`, background: "#fff", color: "#1A1A1A",
        borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer",
        display: "flex", alignItems: "center", gap: 8,
        fontFamily: "inherit",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = INDIGO; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; }}
    >
      {icon}{label}
    </button>
  );
}
function GhostBtn({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 36, padding: "0 14px",
        border: `1px solid ${BORDER}`, background: "#fff", color: INK,
        borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer",
        display: "flex", alignItems: "center", gap: 8,
        fontFamily: "inherit",
      }}
    >
      {icon}{label}
    </button>
  );
}
function IconBtn44({ children, onClick, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        width: 36, height: 36,
        border: `1px solid ${BORDER}`, background: "#fff",
        borderRadius: 8, display: "grid", placeItems: "center",
        cursor: "pointer", color: INDIGO,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = INDIGO; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; }}
    >
      {children}
    </button>
  );
}

function Dropdown({ open, onToggle, onClose, selected, placeholder, onClear, children }) {
  return (
    <Box sx={{ position: "relative" }}>
      {selected ? (
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1,
          height: 36, minWidth: 154, padding: "0 10px",
          border: `1px solid ${INDIGO}`, borderRadius: "6px",
          color: INDIGO, fontSize: 15, background: "#fff",
        }}>
          <CircleDotIcon size={15} />
          <span onClick={onToggle} style={{ flex: 1, whiteSpace: "nowrap", cursor: "pointer" }}>{selected.label}</span>
          <span onClick={(e) => { e.stopPropagation(); onClear(); onClose(); }} style={{ cursor: "pointer" }}>
            <XIcon size={14} />
          </span>
        </Box>
      ) : (
        <Box onClick={onToggle} sx={{
          display: "flex", alignItems: "center", gap: 1,
          height: 36, padding: "0 10px",
          border: `1px solid ${BORDER}`, borderRadius: "6px",
          fontSize: 15, cursor: "pointer", background: "#fff", color: INK,
        }}>
          {placeholder}
        </Box>
      )}
      {open && (
        <Box sx={{
          position: "absolute", top: 46, left: 0, zIndex: 8,
          minWidth: 190, background: "#fff",
          border: `1px solid ${BORDER}`, borderRadius: "8px",
          boxShadow: "0 8px 24px rgba(0,0,0,.08)", p: "4px",
        }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

function DropdownRow({ onClick, children }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1,
        p: "8px 10px", borderRadius: "6px", cursor: "pointer", fontSize: 14,
        color: INK,
        "&:hover": { background: BG_SOFT },
      }}
    >{children}</Box>
  );
}

function MenuRow({ icon, onClick, danger, children }) {
  const fg = danger ? "#D92D3B" : INK;
  const hoverBg = danger ? "#FFF1F2" : BG_SOFT;
  return (
    <Box
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      sx={{
        display: "flex", alignItems: "center", gap: 1,
        p: "8px 10px", borderRadius: "6px", cursor: "pointer",
        fontSize: 14, color: fg,
        "&:hover": { background: hoverBg },
      }}
    >
      {icon}
      {children}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Inline SVG icons (strict Lucide shapes used in the design, no external deps)
// ═══════════════════════════════════════════════════════════════════════

const Svg = ({ size = 16, color = "currentColor", children, onClick, style }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    onClick={onClick} style={style}
  >{children}</svg>
);

const PlugIcon           = (p) => <Svg {...p}><path d="M12 22v-5" /><path d="M9 7V2" /><path d="M15 7V2" /><path d="M6 13V8h12v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4z" /></Svg>;
const DownloadIcon       = (p) => <Svg {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></Svg>;
const PlusIcon           = (p) => <Svg {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Svg>;
const FilterIcon         = (p) => <Svg {...p}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></Svg>;
const SearchIcon         = (p) => <Svg {...p}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></Svg>;
const CircleDotIcon      = (p) => <Svg {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="1" /></Svg>;
const ChevronDownIcon    = (p) => <Svg {...p}><polyline points="6 9 12 15 18 9" /></Svg>;
const RadioIcon          = (p) => <Svg {...p}><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" /><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" /><circle cx="12" cy="12" r="2" /><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" /><path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1" /></Svg>;
const CheckIcon          = (p) => <Svg {...p}><polyline points="20 6 9 17 4 12" /></Svg>;
const RotateCwIcon       = (p) => <Svg {...p}><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></Svg>;
const RotateCcwIcon      = (p) => <Svg {...p}><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></Svg>;
const EllipsisVIcon      = (p) => <Svg {...p}><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></Svg>;
const Settings2Icon      = (p) => <Svg {...p}><path d="M20 7h-9" /><path d="M14 17H5" /><circle cx="17" cy="17" r="3" /><circle cx="7" cy="7" r="3" /></Svg>;
const PauseIcon          = (p) => <Svg {...p}><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></Svg>;
const PlayIcon           = (p) => <Svg {...p}><polygon points="5 3 19 12 5 21 5 3" /></Svg>;
const CopyIcon           = (p) => <Svg {...p}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Svg>;
const Trash2Icon         = (p) => <Svg {...p}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></Svg>;
const XIcon              = (p) => <Svg {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const ChevronLeftIcon    = (p) => <Svg {...p}><polyline points="15 18 9 12 15 6" /></Svg>;
const ChevronRightIcon   = (p) => <Svg {...p}><polyline points="9 18 15 12 9 6" /></Svg>;
